import { currentSession, onAuthChange } from "./auth/auth";
import { mount } from "./lib/dom";
import { renderDashboard } from "./views/dashboard";
import { renderEditor } from "./views/editor";
import { renderLogin } from "./views/login";
import { renderLedger } from "./views/ledger";
import { renderRequests } from "./views/requests";
import { renderShell, type RouteName } from "./views/shell";

const root = document.getElementById("app");
if (!root) throw new Error("Missing #app root");

type Route =
  | { name: "dashboard" }
  | { name: "editor"; reviewId: string | null; requestId: string | null }
  | { name: "requests" }
  | { name: "ledger" };

let route: Route = parseHash();

function parseHash(): Route {
  const hash = window.location.hash.replace(/^#\/?/, "");
  if (!hash || hash === "dashboard") return { name: "dashboard" };
  if (hash === "requests") return { name: "requests" };
  if (hash === "ledger") return { name: "ledger" };
  if (hash === "editor" || hash === "editor/new") return { name: "editor", reviewId: null, requestId: null };
  if (hash.startsWith("editor/new/from-request/")) {
    return {
      name: "editor",
      reviewId: null,
      requestId: hash.slice("editor/new/from-request/".length),
    };
  }
  if (hash.startsWith("editor/")) {
    return { name: "editor", reviewId: hash.slice("editor/".length), requestId: null };
  }
  return { name: "dashboard" };
}

function setHash(next: Route): void {
  switch (next.name) {
    case "dashboard":
      window.location.hash = "/dashboard";
      break;
    case "requests":
      window.location.hash = "/requests";
      break;
    case "ledger":
      window.location.hash = "/ledger";
      break;
    case "editor":
      if (next.requestId) {
        window.location.hash = `/editor/new/from-request/${next.requestId}`;
      } else {
        window.location.hash = next.reviewId ? `/editor/${next.reviewId}` : "/editor/new";
      }
      break;
  }
}

function navigate(name: RouteName, params: Record<string, string> = {}): void {
  if (name === "editor") {
    route = {
      name: "editor",
      reviewId: params.id ?? null,
      requestId: params.requestId ?? null,
    };
  } else if (name === "requests") {
    route = { name: "requests" };
  } else if (name === "ledger") {
    route = { name: "ledger" };
  } else {
    route = { name: "dashboard" };
  }
  setHash(route);
  paint();
}

window.addEventListener("hashchange", () => {
  route = parseHash();
  paint();
});

async function paint(): Promise<void> {
  if (!root) return;
  const session = await currentSession();
  if (!session) {
    renderLogin(root);
    return;
  }
  const routeName: RouteName =
    route.name === "editor"
      ? "editor"
      : route.name === "requests"
        ? "requests"
        : route.name === "ledger"
          ? "ledger"
          : "dashboard";
  let body: HTMLElement;
  switch (route.name) {
    case "dashboard":
      body = renderDashboard({
        onOpen: (id) => navigate("editor", { id }),
        onCreate: () => navigate("editor"),
      });
      break;
    case "editor":
      body = renderEditor({
        reviewId: route.reviewId,
        requestId: route.requestId,
        onSaved: (id) => {
          if (route.name === "editor") {
            route = { name: "editor", reviewId: id, requestId: route.requestId };
            setHash(route);
          }
        },
        onClose: () => navigate("dashboard"),
      });
      break;
    case "requests":
      body = renderRequests();
      break;
    case "ledger":
      body = renderLedger();
      break;
  }
  renderShell(root, { session, route: routeName, onNavigate: navigate }, body);
}

onAuthChange(() => {
  void paint();
});

mount(root);
void paint();
